import {
  collection,
  addDoc,
  getDoc,
  getDocs,
  doc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  updateDoc,
  increment,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../utils/firebaseConfig';
import type { BlogPost, CreateBlogPostData } from '../types/blog';

const BLOGS_COLLECTION = 'blogPosts';

export const blogService = {
  /**
   * Create a new blog post
   */
  async createBlogPost(
    data: CreateBlogPostData,
    authorUid: string,
    authorName: string,
    authorAvatar?: string
  ): Promise<string> {
    const blogData = {
      ...data,
      author: {
        uid: authorUid,
        name: authorName,
        avatar: authorAvatar,
      },
      publishedAt: data.status === 'published' ? Timestamp.now() : null,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      viewCount: 0,
    };

    const docRef = await addDoc(collection(db, BLOGS_COLLECTION), blogData);
    return docRef.id;
  },

  /**
   * Get a single blog post by ID
   */
  async getBlogPost(id: string): Promise<BlogPost | null> {
    const docRef = doc(db, BLOGS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as BlogPost;
  },

  /**
   * Get a blog post by slug
   */
  async getBlogPostBySlug(slug: string): Promise<BlogPost | null> {
    const q = query(
      collection(db, BLOGS_COLLECTION),
      where('slug', '==', slug),
      limit(1)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
    } as BlogPost;
  },

  /**
   * Get all published blog posts
   */
  async getPublishedBlogs(limitCount: number = 20): Promise<BlogPost[]> {
    const q = query(
      collection(db, BLOGS_COLLECTION),
      where('status', '==', 'published'),
      orderBy('publishedAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        } as BlogPost)
    );
  },

  /**
   * Get all blog posts (admin only)
   */
  async getAllBlogs(limitCount: number = 50): Promise<BlogPost[]> {
    const q = query(
      collection(db, BLOGS_COLLECTION),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(
      (doc) =>
        ({
          id: doc.id,
          ...doc.data(),
        } as BlogPost)
    );
  },

  /**
   * Update a blog post
   */
  async updateBlogPost(
    id: string,
    data: Partial<CreateBlogPostData>
  ): Promise<void> {
    const docRef = doc(db, BLOGS_COLLECTION, id);

    const updateData: any = {
      ...data,
      updatedAt: Timestamp.now(),
    };

    // If publishing for the first time
    if (data.status === 'published') {
      const currentDoc = await getDoc(docRef);
      if (currentDoc.exists() && currentDoc.data().status !== 'published') {
        updateData.publishedAt = Timestamp.now();
      }
    }

    await updateDoc(docRef, updateData);
  },

  /**
   * Increment view count
   */
  async incrementViewCount(id: string): Promise<void> {
    const docRef = doc(db, BLOGS_COLLECTION, id);
    await updateDoc(docRef, {
      viewCount: increment(1),
    });
  },

  /**
   * Delete a blog post
   */
  async deleteBlogPost(id: string): Promise<void> {
    const docRef = doc(db, BLOGS_COLLECTION, id);
    await deleteDoc(docRef);
  },

  /**
   * Generate URL-friendly slug from title
   */
  generateSlug(title: string): string {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/--+/g, '-'); // Replace multiple hyphens with single hyphen
  },
};
